import cn from 'classnames';
import { useState, useContext, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import Subheading from 'components/common/Subheading';
import ToggleButton from 'components/common/ToggleButton';
import { VoucherContext } from 'components/signUpFunnel/Contexts';
import { TEMPO_PLAN_MEALS_PER_WEEK } from 'lib/constants/signUp';

import { calculatePricePerServingDetails } from '../helpers/calculatePricePerServingDetails';

import BestFitLabel from './BestFitLabel';

const containerText = {
  subQuestion: 'Your single-serving weekly meal plan',
};

const TempoBoxCustomizer = ({
  weeklyMeals,
  setWeeklyMeals,
  mealPriceDetails,
  boxRecommendations,
  isPickMeals = false,
  setShowBoxOptions,
}) => {
  const [showMorePlans, setShowMorePlans] = useState(false);
  const transitionRef = useRef();

  const boxDisplayOrder = boxRecommendations.displayOrder || TEMPO_PLAN_MEALS_PER_WEEK;

  const initialMealsPerWeek = boxDisplayOrder.slice(0, 4);
  const lastMealsPerWeek = boxDisplayOrder.slice(4);

  const handleShowMoreToggle = () => {
    const newState = !showMorePlans;
    setShowMorePlans(newState);
    if (setShowBoxOptions) {
      setShowBoxOptions(newState);
    }
  };

  return (
    <>
      {!isPickMeals && (
        <Subheading className="mb-4 text-left" brandStylesOverrides="h5">
          {containerText.subQuestion}
        </Subheading>
      )}
      <div
        className={cn('mx-auto flex flex-col gap-2 bp1:mx-0', {
          'pb-4': !showMorePlans,
          'pb-2': showMorePlans,
        })}
        role="radiogroup"
        aria-label="Recipes options"
      >
        {initialMealsPerWeek.map(mealsPerWeek => (
          <MealsPerWeekButton
            key={mealsPerWeek}
            boxRecommendations={boxRecommendations}
            mealsPerWeek={mealsPerWeek}
            mealPriceDetails={mealPriceDetails}
            weeklyMeals={weeklyMeals}
            setWeeklyMeals={setWeeklyMeals}
          />
        ))}
      </div>
      <CSSTransition
        in={showMorePlans}
        timeout={500}
        classNames={{
          enter: 'fadeInOut-enter scaleHeight-enter',
          enterActive: 'fadeInOut-enter-active scaleHeight-enter-active',
          exit: 'fadeInOut-exit scaleHeight-exit',
          exitActive: 'fadeInOut-exit-active scaleHeight-exit-active',
        }}
        unmountOnExit
        nodeRef={transitionRef}
      >
        <div
          className="mx-auto flex flex-col gap-2 bp1:mx-0"
          role="radiogroup"
          aria-label="Recipes options"
          ref={transitionRef}
        >
          {lastMealsPerWeek.map(mealsPerWeek => (
            <MealsPerWeekButton
              key={mealsPerWeek}
              boxRecommendations={boxRecommendations}
              mealsPerWeek={mealsPerWeek}
              mealPriceDetails={mealPriceDetails}
              weeklyMeals={weeklyMeals}
              setWeeklyMeals={setWeeklyMeals}
            />
          ))}
        </div>
      </CSSTransition>
      <button
        className={cn(
          'text flex w-full cursor-pointer justify-end text-tempo-black-100 underline outline-none',
          {
            'mt-4': showMorePlans,
          }
        )}
        onClick={handleShowMoreToggle}
      >
        {showMorePlans ? 'Show Less' : 'Show More'}
      </button>
    </>
  );
};

const MealsPerWeekButton = ({
  boxRecommendations,
  mealsPerWeek,
  mealPriceDetails,
  setWeeklyMeals,
  weeklyMeals,
}) => {
  const { voucher } = useContext(VoucherContext);
  const priceDetail = calculatePricePerServingDetails(mealPriceDetails, mealsPerWeek, voucher);

  return (
    <ToggleButton
      className="relative"
      id={mealsPerWeek}
      selected={mealsPerWeek === weeklyMeals}
      handleClick={() => setWeeklyMeals(mealsPerWeek)}
      type="refreshToggle"
      key={mealsPerWeek}
      aria-label={`${mealsPerWeek} recipes`}
      role="radio"
    >
      {mealsPerWeek === boxRecommendations.recommendedMeals && (
        <BestFitLabel className="left-4 border-yellow-100 bg-yellow-100" />
      )}
      <div className="flex w-full items-center justify-between">
        <span className="text-tempo-black-100">{`${mealsPerWeek} meal plan`}</span>
        <span className="flex items-baseline justify-end whitespace-nowrap">
          <span
            className={cn({
              'font-normal text-charcoal-90 line-through': priceDetail.hasPromo,
              'text-tempo-black-100': !priceDetail.hasPromo,
            })}
          >
            {priceDetail.mealPriceFormatted}
          </span>
          {priceDetail.hasPromo && (
            <span className="ml-2 text-tempo-black-100">{priceDetail.promoMealPriceFormatted}</span>
          )}
          <span className="ml-1 text-tempo-black-100">ea</span>
        </span>
      </div>
    </ToggleButton>
  );
};

TempoBoxCustomizer.propTypes = {
  weeklyMeals: PropTypes.number.isRequired,
  setWeeklyMeals: PropTypes.func.isRequired,
  mealPriceDetails: PropTypes.array,
  boxRecommendations: PropTypes.shape({
    recommendedMeals: PropTypes.number,
    displayOrder: PropTypes.arrayOf(PropTypes.number),
  }),
  isPickMeals: PropTypes.bool,
  setShowBoxOptions: PropTypes.func,
};

MealsPerWeekButton.propTypes = {
  weeklyMeals: PropTypes.number.isRequired,
  setWeeklyMeals: PropTypes.func.isRequired,
  mealPriceDetails: PropTypes.array,
  boxRecommendations: PropTypes.shape({
    recommendedMeals: PropTypes.number,
    displayOrder: PropTypes.arrayOf(PropTypes.number),
  }),
  mealsPerWeek: PropTypes.number.isRequired,
};

export default TempoBoxCustomizer;
